import * as styles from "./Tour.css";

import { waapi } from "animejs";
import { type CSSProperties, type JSX, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useFloatingPortalContainer } from "@frontend/contexts";
import { TRANSITION_NORMAL_MS } from "@frontend/styles/constants";

import { TOUR_PADDING, TOUR_RADIUS } from "./constants";

type Box = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type TourOverlayProps = {
  rect: DOMRect | null;
  step: number;
  padding?: number;
  radius?: number;
  reachable?: boolean;
};

// Four panes around the cutout, so the target itself is never covered. A fifth
// one over it is what makes an ordinary step block the page.
function blockersFor(box: Box): CSSProperties[] {
  const top = Math.max(0, box.top);
  const height = Math.max(0, box.height + Math.min(0, box.top));

  return [
    { left: 0, right: 0, top: 0, height: top },
    { left: 0, right: 0, top: top + height, bottom: 0 },
    { left: 0, top, width: Math.max(0, box.left), height },
    { left: Math.max(0, box.left + box.width), right: 0, top, height },
  ];
}

function boxFor(rect: DOMRect | null, padding: number): Box {
  // No target: a hole with no size, whose shadow then covers the whole viewport
  if (!rect) {
    return {
      left: window.innerWidth / 2,
      top: window.innerHeight / 2,
      width: 0,
      height: 0,
    };
  }

  return {
    left: rect.left - padding,
    top: rect.top - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
}

function isSameBox(a: Box, b: Box): boolean {
  return (
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height
  );
}

export function TourOverlay({
  rect,
  step,
  padding = TOUR_PADDING,
  radius = TOUR_RADIUS,
  reachable = false,
}: TourOverlayProps): JSX.Element {
  const container = useFloatingPortalContainer();

  const spotlightRef = useRef<HTMLDivElement>(null);
  const previous = useRef<{ step: number; box: Box } | null>(null);

  const box = boxFor(rect, padding);

  useEffect(() => {
    const element = spotlightRef.current;

    if (!element) {
      return;
    }

    const last = previous.current;

    // The step changes one render before the new rect is measured, so the box
    // still describes the step we are leaving. Leave it recorded as such.
    if (last && isSameBox(last.box, box)) {
      return;
    }

    previous.current = { step, box };

    // Within a step the cutout is glued to a target that scrolls, so it snaps.
    // Only the move from one step to the next is worth animating.
    if (
      !last ||
      last.step === step ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    waapi.animate(element, {
      left: [`${last.box.left}px`, `${box.left}px`],
      top: [`${last.box.top}px`, `${box.top}px`],
      width: [`${last.box.width}px`, `${box.width}px`],
      height: [`${last.box.height}px`, `${box.height}px`],
      duration: TRANSITION_NORMAL_MS,
      ease: "outQuad",
      // A finished WAAPI animation keeps its fill, which would outrank the
      // inline style the tracker rewrites on every scroll. Hand the element
      // back once the move has landed.
      onComplete: (animation) => animation.cancel(),
    });
  }, [step, box]);

  return createPortal(
    <div className={styles.overlay}>
      <div
        ref={spotlightRef}
        className={styles.spotlight}
        style={{
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          borderRadius: radius,
        }}
      />

      {blockersFor(box).map((pane, index) => (
        <div key={index} className={styles.blocker} style={pane} />
      ))}

      {!reachable && (
        <div
          className={styles.blocker}
          style={{
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
          }}
        />
      )}
    </div>,
    container ?? document.body,
  );
}
